"use client";

/**
 * @module Popover
 *
 * Floating content panel anchored to a trigger element. Built on Radix UI Popover primitive.
 * Supports controlled/uncontrolled modes, custom positioning, and focus management.
 *
 * @see {@link https://ui.shadcn.com/docs/components/popover Shadcn Popover}
 * @see {@link https://www.radix-ui.com/primitives/docs/components/popover Radix Popover}
 *
 * @example
 * // Basic popover
 * <Popover>
 *   <PopoverTrigger asChild>
 *     <Button>Open Popover</Button>
 *   </PopoverTrigger>
 *   <PopoverContent>
 *     <p>Popover content here</p>
 *   </PopoverContent>
 * </Popover>
 *
 * @example
 * // Controlled popover with custom positioning
 * const [open, setOpen] = useState(false);
 *
 * <Popover open={open} onOpenChange={setOpen}>
 *   <PopoverTrigger>Settings</PopoverTrigger>
 *   <PopoverContent align="start" sideOffset={8}>
 *     <SettingsForm onSave={() => setOpen(false)} />
 *   </PopoverContent>
 * </Popover>
 *
 * @example
 * // With custom anchor point
 * <Popover>
 *   <PopoverAnchor asChild>
 *     <div>Anchor element (popover positions relative to this)</div>
 *   </PopoverAnchor>
 *   <PopoverTrigger>Open</PopoverTrigger>
 *   <PopoverContent>Content</PopoverContent>
 * </Popover>
 */
import { Popover as PopoverPrimitive } from "radix-ui";
import type * as React from "react";
import type { Ref } from "react";
import { cn } from "../lib/utils";

/** Root component that manages popover open/closed state. */
const Popover = PopoverPrimitive.Root;

/** Element that toggles the popover when clicked. Use `asChild` to wrap custom elements. */
const PopoverTrigger = PopoverPrimitive.Trigger;

/** Alternative anchor point for popover positioning (separate from trigger). */
const PopoverAnchor = PopoverPrimitive.Anchor;

/**
 * Popover content container. Animated on open/close.
 * @param align - Horizontal alignment: `"start"`, `"center"` (default), or `"end"`
 * @param sideOffset - Distance from anchor in pixels (default: 4)
 * @param forceMount - Keep mounted in DOM even when closed
 */
const PopoverContent = ({
  className,
  forceMount,
  align = "center",
  sideOffset = 4,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
  ref?: Ref<React.ElementRef<typeof PopoverPrimitive.Content>>;
}) => <PopoverPrimitive.Portal forceMount={forceMount} data-sentry-element="PopoverPrimitive.Portal" data-sentry-component="PopoverContent" data-sentry-source-file="popover.tsx">
    <PopoverPrimitive.Content align={align} className={cn("data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1 z-50 origin-[var(--radix-popover-content-transform-origin)] rounded-md border border-gray-150 border-solid bg-white fill-mode-forwards p-4 text-gray-950 shadow-md outline-hidden data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:opacity-0 data-[state=open]:opacity-100 dark:border-gray-800 dark:bg-gray-875 dark:text-gray-50", className)} ref={ref} sideOffset={sideOffset} {...props} data-sentry-element="PopoverPrimitive.Content" data-sentry-source-file="popover.tsx" />
  </PopoverPrimitive.Portal>;
export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };